import { EllipsisTypography } from "@lyfegen-platform/react-common";
import { byAsc, CountryName, distinctBy, sortByComparisons } from "@lyfegen-platform/utils";
import { Box, Button, Divider, Typography, useTheme } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../../../components/Card";
import CardHeader from "../../../../components/CardHeader";
import LyfeTooltip from "../../../../components/LyfeTooltip";
import MappedComponent from "../../../../components/mapped-components/MappedComponent";
import { AgreementSubjectMatter, Maybe, PriceCategory, PriceType } from "../../../../models/generated/graphql";
import renderCurrency from "../../../../utils/currency";
import formatCurrency from "../../../../utils/currency-format";
import { firstLetterUppercase } from "../../../../utils/first-letter-uppercase";
import { isEmptyValue } from "../../../../utils/is-empty-value";
import startUpper from "../../../../utils/start-upper";
import useStyles from "../../agreements-styles";
import { CardTypes } from "../Details";
import { GetAgreementDetailsQuery } from "../types/queries.generated";

const defaultCollapsedRowsCount = 10;

interface UpdateHash {
	updateHash?: (el: CardTypes) => void;
}

export interface ScenarioTreatment {
	priceRate?: number;
	treatmentName?: string;
	basePrice?: number;
}

export interface SubItem {
	bullet?: string;
	content?: string;
}

export interface ScenarioItem {
	content?: string;
	subItems: Array<SubItem> | undefined;
}

export interface TreatmentMasterDataPriceItem {
	validFrom: Date;
	validTo?: Date;
	price?: number;
}

export interface TreatmentCanadianMasterDataPriceItem {
	priceCategory: PriceCategory;
	priceItem: TreatmentMasterDataPriceItem[];
}

export interface TreatmentPriceItem {
	name: string;
	customPrice?: number;
	basePriceType?: Maybe<PriceType>;
	exFactoryPrices?: Array<TreatmentMasterDataPriceItem>;
	publicPrices?: Array<TreatmentMasterDataPriceItem>;
	avgGenericPrices?: Array<TreatmentMasterDataPriceItem>;
	crossCountryPrices?: Array<TreatmentMasterDataPriceItem>;
	canadianPrices?: Array<TreatmentCanadianMasterDataPriceItem>;
}

export interface ScenariosProps {
	defaultPriceRate: number;
	subjectMatter: AgreementSubjectMatter;
	scenarios?: {
		treatmentPrices: Array<TreatmentPriceItem>;
		items?: Array<ScenarioItem>;
		validity?: {
			startDate?: string;
			endDate?: string;
			version?: string;
		};
	};
	isVisible: boolean;
	currency: GetAgreementDetailsQuery["baseAgreements"][0]["currency"] | null;
	countryName: GetAgreementDetailsQuery["baseAgreements"][0]["country"]["name"];
}

export type DiscountCapsTableDataProps = {
	scopeName: string | undefined | null | false;
	discountCaps: string | undefined | null | false;
};

export const resolveContent = (content: string | undefined | any) => {
	const values = content?.split("/");
	const renderArray: any = [];

	renderArray.push(<EllipsisTypography text={values[0]} truncate />);

	values.slice(1).forEach((val: string) => {
		if (val.includes("*")) {
			const itemArray = val.split("*");
			const tooltipProps = {
				title: itemArray.join(", "),
				children: (
					<Typography variant="inherit">
						{itemArray[0]}, +{itemArray.length - 1}
					</Typography>
				),
			};

			renderArray.push(<MappedComponent component="Tooltip" props={tooltipProps} />);
		} else {
			renderArray.push(val);
		}
	});

	return renderArray;
};

export const resolveContentForTooltip = (content: string) => {
	const values = content?.split("/");
	return values
		.map((val: string) => {
			if (val.includes("*")) {
				const itemArray = val.split("*");
				return `${itemArray[0]}, +${itemArray.length - 1}`;
			} else {
				return val;
			}
		})
		.join(" ");
};

const MasterDataPriceContent: React.FC<{
	priceTooltip: JSX.Element | "";
	price: string;
	isVisible: boolean;
	currency: GetAgreementDetailsQuery["baseAgreements"][0]["currency"] | null;
}> = (props) => {
	return (
		<Box textAlign="right">
			<LyfeTooltip title={props.priceTooltip} placement="right-start">
				<Typography display="inline">{props.price}</Typography>
			</LyfeTooltip>
			{props.isVisible && (
				<>
					{" "}
					<LyfeTooltip title={props.currency?.Name || "Swiss franc"} placement="right-start">
						<Typography display="inline">{props.currency?.Code || "CHF"}</Typography>
					</LyfeTooltip>
				</>
			)}
		</Box>
	);
};

const ScenariosCard: React.FC<ScenariosProps & UpdateHash> = (props) => {
	const { t } = useTranslation();
	const { tableRoot, vaTop, w600, mr1 } = useStyles();
	const { spacing, palette } = useTheme();
	const [arePricesCollapsed, setPricesCollapsed] = useState(true);

	const getPricesWithFormattedCanadianValues = (): TreatmentPriceItem[] | undefined => {
		return props.scenarios?.treatmentPrices.map((treatmentPrice) => {
			const normalizedPrices: TreatmentCanadianMasterDataPriceItem[] = canadianPricesTableHeaders.map((priceCategory) => {
				const foundPrice = treatmentPrice.canadianPrices?.find((canadianPrice) => canadianPrice.priceCategory === priceCategory);

				return foundPrice
					? foundPrice
					: {
							priceCategory,
							priceItem: [],
					  };
			});

			return {
				...treatmentPrice,
				canadianPrices: normalizedPrices,
			};
		});
	};

	const isCustomPricePresent = props.scenarios?.treatmentPrices.some((treatmentPrice) => !isEmptyValue(treatmentPrice.customPrice));
	const isMasterDataPricesPresent =
		props.subjectMatter === AgreementSubjectMatter.Drugs &&
		props.scenarios?.treatmentPrices.some(
			(treatmentPrice) => treatmentPrice.publicPrices?.length || treatmentPrice.exFactoryPrices?.length
		);

	const canadianPrices = props.scenarios?.treatmentPrices
		.filter((treatmentPrice) => treatmentPrice.canadianPrices?.length)
		.flatMap((treatmentPrice) => treatmentPrice.canadianPrices!);
	const areCanadianPricesPresent = props.countryName === CountryName.Canada && !!canadianPrices?.length;
	const canadianPricesTableHeaders =
		areCanadianPricesPresent && canadianPrices.length
			? distinctBy(
					canadianPrices.map((listPrice) => listPrice.priceCategory),
					(price) => price
			  ).sort(sortByComparisons(byAsc((price) => price)))
			: [];
	const formattedPrices = getPricesWithFormattedCanadianValues();

	const isAvgGenericPricePresent = props.scenarios?.treatmentPrices.some(
		(treatmentPrice) => !isEmptyValue(treatmentPrice.avgGenericPrices)
	);
	const isCrossCountryPricePresent = props.scenarios?.treatmentPrices.some(
		(treatmentPrice) => !isEmptyValue(treatmentPrice.crossCountryPrices)
	);
	const tableHeaders = [
		"default-price-rate",
		"treatment-name",
		...(isCustomPricePresent ? ["custom-price"] : []),
		...canadianPricesTableHeaders,
		...(isMasterDataPricesPresent ? ["public-price", "ex-factory-price"] : []),
		...(isAvgGenericPricePresent ? ["average-generic-price"] : []),
		...(isCrossCountryPricePresent ? ["cross-country-price"] : []),
	];
	const id = "scenarios";

	function formatPrice(price: number | undefined, showWithoutCurrencyCode?: boolean) {
		if (!props.isVisible) return "XX";
		if (showWithoutCurrencyCode) {
			return formatCurrency(price, 2, 2);
		}

		return renderCurrency(price, `${props.currency?.Code}`, 2, 2);
	}

	function formatMasterDataPriceValidity(priceItem: TreatmentMasterDataPriceItem, addValue: boolean) {
		return [
			...(addValue ? [`${formatPrice(priceItem.price)}: `] : [firstLetterUppercase(t("valid-from"))]),
			format(priceItem.validFrom, "yyyy-MM-dd"),
			...(priceItem.validTo ? [t("to"), format(priceItem.validTo, "yyyy-MM-dd")] : []),
		].join(" ");
	}

	function renderMasterDataPriceTooltip(priceItems: Array<TreatmentMasterDataPriceItem>) {
		if (!props.isVisible || priceItems.length === 0) return "";

		return (
			<Box>
				{priceItems.map((priceItem, index) => (
					<>
						{index === 1 && <Typography>{t("other-valid-prices")}</Typography>}
						<Typography key={index}>{formatMasterDataPriceValidity(priceItem, index > 0)}</Typography>
					</>
				))}
			</Box>
		);
	}

	return (
		<Card
			style={{
				marginTop: spacing(2),
				borderRadius: "4px",
			}}
			id={id}
			onPointerOver={() => props.updateHash && props.updateHash(id)}
		>
			<CardHeader title={t(id).toLocaleUpperCase()} />

			<Box className={tableRoot}>
				<table style={{ tableLayout: "fixed" }}>
					<colgroup>
						<col width="17%" />
						<col width="32%" />
						<col width="17%" span={3} />
					</colgroup>
					<thead>
						<tr>
							{tableHeaders.map((h, i) => (
								<td key={i}>
									<Typography variant="subtitle2" color="textSecondary" align={i > 1 ? "right" : "left"}>
										{t(h)}
									</Typography>
								</td>
							))}
						</tr>
					</thead>
					<tbody>
						{formattedPrices?.slice(0, arePricesCollapsed ? defaultCollapsedRowsCount : undefined).map((treatmentPrice, i) => (
							<tr key={i}>
								<td className={vaTop}>
									<Typography>{props.isVisible ? `${props.defaultPriceRate} %` : "XX"}</Typography>
								</td>
								<td className={vaTop}>
									<Typography>{treatmentPrice.name}</Typography>
								</td>
								{isCustomPricePresent && (
									<LyfeTooltip title={props.currency?.Name || "Swiss franc"} placement="right-start">
										<Typography align="right">{formatPrice(treatmentPrice.customPrice)}</Typography>
									</LyfeTooltip>
								)}
								{areCanadianPricesPresent &&
									treatmentPrice.canadianPrices?.map((canadianPrice, i) => (
										<td className={vaTop} key={i}>
											{!isEmptyValue(treatmentPrice.basePriceType) ? (
												<MasterDataPriceContent
													priceTooltip={renderMasterDataPriceTooltip(canadianPrice.priceItem)}
													price={formatPrice(canadianPrice.priceItem?.[0]?.price, true)}
													isVisible={props.isVisible}
													currency={props.currency}
												/>
											) : (
												<Typography align="right">{formatPrice(0)}</Typography>
											)}
										</td>
									))}
								{isMasterDataPricesPresent && (
									<>
										<td className={vaTop}>
											{!isEmptyValue(treatmentPrice.basePriceType) ? (
												<MasterDataPriceContent
													priceTooltip={renderMasterDataPriceTooltip(treatmentPrice.publicPrices!)}
													price={formatPrice(treatmentPrice.publicPrices?.[0]?.price, true)}
													isVisible={props.isVisible}
													currency={props.currency}
												/>
											) : (
												<Typography align="right">{formatPrice(0)}</Typography>
											)}
										</td>
										<td className={vaTop}>
											{!isEmptyValue(treatmentPrice.basePriceType) ? (
												<MasterDataPriceContent
													priceTooltip={renderMasterDataPriceTooltip(treatmentPrice.exFactoryPrices!)}
													price={formatPrice(treatmentPrice.exFactoryPrices?.[0]?.price, true)}
													isVisible={props.isVisible}
													currency={props.currency}
												/>
											) : (
												<Typography align="right">{formatPrice(0)}</Typography>
											)}
										</td>
										{isAvgGenericPricePresent && (
											<td className={vaTop}>
												{!isEmptyValue(treatmentPrice.basePriceType) ? (
													<MasterDataPriceContent
														priceTooltip={renderMasterDataPriceTooltip(treatmentPrice.avgGenericPrices!)}
														price={formatPrice(treatmentPrice.avgGenericPrices?.[0]?.price, true)}
														isVisible={props.isVisible}
														currency={props.currency}
													/>
												) : (
													<Typography align="right">{formatPrice(0)}</Typography>
												)}
											</td>
										)}
										{isCrossCountryPricePresent && (
											<td className={vaTop}>
												{!isEmptyValue(treatmentPrice.basePriceType) ? (
													<MasterDataPriceContent
														priceTooltip={renderMasterDataPriceTooltip(treatmentPrice.crossCountryPrices!)}
														price={formatPrice(treatmentPrice.crossCountryPrices?.[0]?.price, true)}
														isVisible={props.isVisible}
														currency={props.currency}
													/>
												) : (
													<Typography align="right">{formatPrice(0)}</Typography>
												)}
											</td>
										)}
									</>
								)}
							</tr>
						))}
						{props.scenarios?.treatmentPrices && props.scenarios.treatmentPrices.length > defaultCollapsedRowsCount && (
							<Button
								style={{ marginTop: "10px" }}
								color="primary"
								endIcon={arePricesCollapsed ? <ExpandMore /> : <ExpandLess />}
								onClick={() => setPricesCollapsed(!arePricesCollapsed)}
							>
								{t(arePricesCollapsed ? "view-all" : "view-less")}
							</Button>
						)}
					</tbody>
				</table>
			</Box>
			{props.isVisible && (
				<Box display="flex" width="100%" pl={1} paddingTop={2} alignItems="center" justifyContent="space-between">
					<Box display="flex" pl={3} justifyContent="flex-start" alignItems="center">
						<Typography className={`${mr1}`} style={{ color: palette.widgetHeaderText.main, fontSize: "14px" }} align="left">
							{t("pricing-scenarios").toUpperCase()}
						</Typography>
					</Box>
					<Divider variant="middle" style={{ flexGrow: 1, backgroundColor: palette.widgetHeaderText.main }} />
				</Box>
			)}
			{props.isVisible && props.scenarios?.validity && (
				<Box display="flex" width="100%" pl={4} pb={2} paddingTop={2} alignItems="center">
					<Typography className={w600}>
						{props.scenarios?.validity?.startDate} - {props.scenarios?.validity?.endDate || "..."}
					</Typography>
				</Box>
			)}
			{props.isVisible &&
				props.scenarios?.items?.map((s: ScenarioItem, i: number) => (
					<Box key={i} pl={4} pt={1} pb={2} width="100%">
						<Typography align="left" className={`${w600} ${mr1}`} variant="body2" color="textSecondary">{`${
							i + 1
						}. ${startUpper(t("scenario"))}`}</Typography>
						{s.content && <Typography>{s.content}</Typography>}
						{s.subItems &&
							s.subItems.map((si: SubItem, idx) => (
								<Box display="flex" width="100%" key={idx} mt={1}>
									<Box paddingRight={2} width="10%">
										<Typography>{si.bullet}</Typography>
									</Box>
									<Box paddingRight={2} width="90%">
										<Typography>{resolveContent(si.content)}</Typography>
									</Box>
								</Box>
							))}
					</Box>
				))}
		</Card>
	);
};

export default ScenariosCard;
